import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom';
import DropdownSelect from "components/Dropdown/DropdownSelect";
import useIsMobile from "hooks/isMobile";
import { apiFetch } from "api";

export default function Leaderboard() {

    const { leagueId } = useParams();

    const [selectedSeason, setSelectedSeason] = useState("")
    const [sortKey, setSortKey] = useState("position")
    const [sortDirection, setSortDirection] = useState("asc")
    const [leaderboardData, setLeaderboardData] = useState({ leaderboard: [] })
    const [seasons, setSeasons] = useState([])
    const [scrolled, setScrolled] = useState(false);

    const rows = leaderboardData?.leaderboard || [];
    const isMobile = useIsMobile();

    const hiddenHeaderKeys = [];

    const headers = [
        { key: "position", value: "#" },
        { key: "player", value: "NAMN" },
        { key: "points", value: "POÄNG*" },
        { key: "netScore", value: "NETTO" },
        { key: "grossScore", value: "BRUTTO" },
        { key: "birdies", value: "BIRDIES" },
        { key: "eagles", value: "EAGLES" },
        { key: "scoreCount", value: "SPELADE" },
        { key: "wins", value: "VINSTER" },
        { key: "prizeMoney", value: "INSPELAT" }
    ];

    const visibleHeaders = isMobile
        ? headers.filter(h => !hiddenHeaderKeys.includes(h.key))
        : headers;

    const sortedRows = [...rows].sort((a, b) => {
        const dir = sortDirection === "asc" ? 1 : -1;
        if (sortKey === "player") {
            const aName = `${a.firstName || ""} ${a.lastName || ""}`.trim();
            const bName = `${b.firstName || ""} ${b.lastName || ""}`.trim();
            return aName.localeCompare(bName) * dir;
        }
        if (sortKey === "points") {
            const aVal = Number(a.points || 0) + Number(a.extraPoints || 0);
            const bVal = Number(b.points || 0) + Number(b.extraPoints || 0);
            return (aVal - bVal) * dir;
        }
        return (Number(a[sortKey] ?? 0) - Number(b[sortKey] ?? 0)) * dir;
    });

    function handleSort(key) {
        if (key === sortKey) {
            setSortDirection(d => d === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key);
            setSortDirection(key === "position" ? "asc" : "desc");
        }
    }

    function handleMobileSort(item) {
        const key = item.key;
        if (key === sortKey) {
            setSortDirection(d => d === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key);
            setSortDirection(key === "position" ? "asc" : "desc");
        }
    }

    function handleScroll(e) {
        setScrolled(e.currentTarget.scrollLeft > 60);
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const seasonsRes = await apiFetch(`/league/${leagueId}/seasons`)
                if (!seasonsRes.ok) throw new Error(`Season fetch HTTP error! Status: ${seasonsRes.status}`);
                const seasons = await seasonsRes.json();
                setSeasons(seasons);
            } catch (err) {
                console.error("Failed to fetch seasons.", err)
            }
        }
        fetchData();
    }, [leagueId])

    useEffect(() => {
        if (seasons.length > 0 && !selectedSeason) {
            setSelectedSeason(seasons[0].id);
        }
    }, [seasons, selectedSeason])

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (selectedSeason === "") return;
                const leaderboardRes = await apiFetch(`/league/${leagueId}/season/${selectedSeason}/leaderboard`)
                if (!leaderboardRes.ok) throw new Error(`Leaderboard fetch HTTP error! Status: ${leaderboardRes.status}`);
                const data = await leaderboardRes.json();
                setLeaderboardData(data);
            } catch (err) {
                console.error("Failed to fetch season or leaderboard.", err)
            }
        }
        fetchData();
    }, [selectedSeason, leagueId])

    const sortIndicator = (key) => {
        if (key !== sortKey) return null;
        return sortDirection === "asc" ? " ↑" : " ↓";
    }

    return (
        <>
            <div className="leaderboard-selectors form-input-grid form-input-grid--2">
                <div className="select-wrapper">
                    <label>Säsong</label>
                    <DropdownSelect
                        items={seasons}
                        value={selectedSeason}
                        onChange={(item) => setSelectedSeason(item.id)}
                        placeholder="Välj säsong"
                    />
                </div>

                <div className="select-wrapper">
                    <label>Sortera</label>
                    <DropdownSelect
                        items={visibleHeaders}
                        value={sortKey}
                        onChange={handleMobileSort}
                        placeholder="Sortera"
                    />
                </div>
            </div>

            <div
                className={`table-wrapper ${scrolled ? "isScrolled" : ""}`}
                onScroll={handleScroll}
            >
                <table className='table table--scrollable leaderboard'>
                    <thead>
                        <tr>
                            {visibleHeaders.map(header => (
                                <th
                                    key={header.key}
                                    onClick={!isMobile ? () => handleSort(header.key) : undefined}
                                    style={!isMobile ? { cursor: 'pointer', userSelect: 'none' } : undefined}
                                >
                                    {header.value}{!isMobile && sortIndicator(header.key)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedRows.map((item, index) => (
                            <tr key={index}>
                                {visibleHeaders.map(header => (
                                    <td className={`${item.position && 'position-' + item.position}`} key={header.key}>
                                        {header.key === "player"
                                            ? `${item.firstName || ""} ${item.lastName || ""}`.trim()
                                            : header.key === "points"
                                                ? `${Number(item.points || 0) + Number(item.extraPoints || 0)} (${Number(item.extraPoints || 0)})`
                                                : header.key === "position"
                                                    ? item.position
                                                    : header.key === "prizeMoney"
                                                        ? `${item[header.key]} kr`
                                                        : item[header.key]}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="leaderboard-info">
                <p>* Totalt antal poäng av 8 bästa resultaten</p>
                <p>* Extra poäng för mest birdies under en runda visas inom ()</p>
            </div>
        </>
    )
}
